import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { EyeOff, Lightbulb, Loader2, Plus, Star } from "lucide-react";
import { DrugSearchResult, addCustomDrug, toggleDrugFavourite } from "@/lib/api";
import { hideDrug, unhideDrug, useHiddenDrugs } from "@/hooks/useHiddenDrugs";
import { useDrugSearch } from "@/hooks/useDrugSearch";
import { UI_CONSTANTS } from "@/lib/constants";
import { useTipPrefs } from "@/hooks/useTipPrefs";

// In-context tip: suggest starring a medicine the doctor keeps prescribing
const FAV_TIP_ID = "ctx-favourites";
const FAV_TIP_MIN_USES = 5;

interface DrugSearchInputProps {
    value: string;
    onChange: (drug: DrugSearchResult) => void;
    onCustomInput?: (value: string) => void;
    placeholder?: string;
    className?: string;
    onEnterNext?: () => void;
    subtitle?: string;
    /** Saved templates offered after the drugs when their name is typed (the add box of the prescription pad) */
    templateOptions?: { id: string; title: string; hint: string }[];
    onPickTemplate?: (id: string) => void;
}

/** Star / hide buttons on a search result row: readable on plain and highlighted rows, tablet-sized tap area */
const rowActionClass = (highlighted: boolean) => cn(
    "-my-1.5 flex h-8 w-8 items-center justify-center rounded transition-colors [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10",
    highlighted
        ? "text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/15"
        : "text-muted-foreground hover:text-foreground hover:bg-accent"
);

const DrugSearchInput = forwardRef<HTMLInputElement, DrugSearchInputProps>(
    ({ value, onChange, onCustomInput, placeholder = "Search medicine...", className, onEnterNext, subtitle, templateOptions, onPickTemplate }, ref) => {
        const [inputValue, setInputValue]       = useState(value ?? "");
        // searchQuery is separate from inputValue: it only updates when the user
        // types, not when a drug is selected. This prevents an extra search API
        // call firing for the brand name after selection.
        const [searchQuery, setSearchQuery]     = useState("");
        const [isOpen, setIsOpen]               = useState(false);
        const [isFocused, setIsFocused]         = useState(false);
        const [highlightedIndex, setHighlightedIndex] = useState(0);

        const containerRef = useRef<HTMLDivElement>(null);
        const dropdownRef  = useRef<HTMLDivElement>(null);
        const optionRefs   = useRef<(HTMLDivElement | null)[]>([]);

        const { drugs: searchResults, isLoading } = useDrugSearch(searchQuery);
        // Hidden medicines never come back from the server. Ones hidden from the current results stay
        // in place struck through with Undo until the next search; unhiding later is in Settings.
        const hidden = useHiddenDrugs();
        const [justHidden, setJustHidden] = useState<number[]>([]);
        useEffect(() => { setJustHidden([]); }, [searchQuery]);
        const drugs = useMemo(
            () => searchResults.filter((d) => !hidden.some((h) => h.id === d.id)),
            [searchResults, hidden]
        );
        // Templates whose name contains what is typed. They come after the drugs and are picked only
        // after an arrow key (or a click), so a quick Enter still adds the drug or the typed name.
        const typedName = inputValue.trim().toLowerCase();
        const templates = useMemo(
            () => (templateOptions && typedName.length >= 2
                ? templateOptions.filter((t) => t.title.toLowerCase().includes(typedName)).slice(0, 3)
                : []),
            [templateOptions, typedName]
        );
        const optionCount = drugs.length + templates.length;
        const [arrowed, setArrowed] = useState(false);
        const pickTemplate = (id: string) => {
            setInputValue("");
            setSearchQuery("");
            onCustomInput?.("");
            setIsOpen(false);
            onPickTemplate?.(id);
        };
        // Star toggles made from this dropdown, applied over the search results
        const [favOverrides, setFavOverrides] = useState<Record<number, boolean>>({});
        const [adding, setAdding] = useState(false);
        const isFav = (drug: DrugSearchResult) => favOverrides[drug.id] ?? !!drug.is_favourite;
        const [starFeedback, setStarFeedback] = useState<{ id: number; added: boolean } | null>(null);
        const tips = useTipPrefs();
        const favTipDrug = tips.isActive(FAV_TIP_ID)
            ? drugs.find((d) => d.usage_count >= FAV_TIP_MIN_USES && !isFav(d))
            : undefined;

        const handleToggleFavourite = async (drug: DrugSearchResult, e: React.MouseEvent) => {
            e.preventDefault();   // keep focus in the input
            e.stopPropagation();  // don't select the drug
            const next = !isFav(drug);
            if (next) tips.markSeen(FAV_TIP_ID); // they've found the star — tip done
            setFavOverrides((m) => ({ ...m, [drug.id]: next }));
            // Brief confirmation in the row so the doctor sees the star did something
            setStarFeedback({ id: drug.id, added: next });
            setTimeout(() => setStarFeedback((f) => (f?.id === drug.id ? null : f)), 2000);
            try { await toggleDrugFavourite(drug.id); }
            catch { setFavOverrides((m) => ({ ...m, [drug.id]: !next })); }
        };

        const handleHide = async (drug: DrugSearchResult, e: React.MouseEvent) => {
            e.preventDefault();   // keep focus in the input
            e.stopPropagation();  // don't select the drug
            setJustHidden((ids) => [...ids, drug.id]);
            try {
                await hideDrug({ id: drug.id, brand_name: drug.brand_name, composition_raw: drug.composition_raw, form: drug.form });
            } catch {
                setJustHidden((ids) => ids.filter((id) => id !== drug.id)); // store rolled back
            }
        };

        const handleUndoHide = async (drugId: number, e: React.MouseEvent) => {
            e.preventDefault();   // keep focus in the input
            e.stopPropagation();
            try {
                await unhideDrug(drugId);
                setJustHidden((ids) => ids.filter((id) => id !== drugId));
            } catch { /* store rolled back — stays hidden */ }
        };

        // Save a medicine missing from the catalog to "My medicines" and select it
        const handleAddToMyMedicines = async (e: React.MouseEvent) => {
            e.preventDefault();
            const name = inputValue.trim();
            if (!name || adding) return;
            setAdding(true);
            try {
                const drug = await addCustomDrug({ brand_name: name });
                if (drug) handleSelect(drug, e);
            } catch {
                // Keep the typed text as a one-off medicine
            } finally {
                setAdding(false);
            }
        };

        useEffect(() => { setInputValue(value ?? ""); }, [value]);
        useEffect(() => { setHighlightedIndex(0); }, [drugs]);

        // Re-position the dropdown when the on-screen keyboard (tablets) resizes the visible viewport
        const [, setViewportTick] = useState(0);
        useEffect(() => {
            const vv = window.visualViewport;
            if (!isOpen || !vv) return;
            const onChange = () => setViewportTick((t) => t + 1);
            vv.addEventListener("resize", onChange);
            vv.addEventListener("scroll", onChange);
            return () => {
                vv.removeEventListener("resize", onChange);
                vv.removeEventListener("scroll", onChange);
            };
        }, [isOpen]);

        // Scroll highlighted item into view
        useEffect(() => {
            if (isOpen && optionRefs.current[highlightedIndex] && dropdownRef.current) {
                const el       = optionRefs.current[highlightedIndex];
                const dropdown = dropdownRef.current;
                if (el) {
                    if (el.offsetTop < dropdown.scrollTop)
                        dropdown.scrollTop = el.offsetTop;
                    else if (el.offsetTop + el.offsetHeight > dropdown.scrollTop + dropdown.clientHeight)
                        dropdown.scrollTop = el.offsetTop + el.offsetHeight - dropdown.clientHeight;
                }
            }
        }, [highlightedIndex, isOpen]);

        useEffect(() => {
            optionRefs.current = optionRefs.current.slice(0, drugs.length);
            if (highlightedIndex >= optionCount && optionCount > 0)
                setHighlightedIndex(optionCount - 1);
        }, [drugs.length, optionCount, highlightedIndex]);

        const handleSelect = (drug: DrugSearchResult, e: React.MouseEvent) => {
            e.preventDefault();
            setInputValue(drug.brand_name);
            setSearchQuery("");   // stop searching — don't fire a search for the brand name
            onChange(drug);
            setIsOpen(false);
            if (onEnterNext) setTimeout(onEnterNext, 100);
        };

        const showDropdown =
            isOpen && inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH;

        // Counts toward the "at most N days" limit, so an ignored tip retires by itself
        const favTipVisible = showDropdown && !isLoading && !!favTipDrug;
        const { recordView } = tips;
        useEffect(() => { if (favTipVisible) recordView(FAV_TIP_ID); }, [favTipVisible, recordView]);

        const getDropdownStyle = (): React.CSSProperties => {
            if (!containerRef.current) return {};
            const rect = containerRef.current.getBoundingClientRect();
            const minWidth = Math.max(rect.width, 280);
            const dropdownHeight = Math.min(drugs.length * 56 + 16, 256);
            // Visible bottom edge — excludes the on-screen keyboard on tablets
            const visibleBottom = window.visualViewport
                ? window.visualViewport.offsetTop + window.visualViewport.height
                : window.innerHeight;
            const wouldOverflowBottom = rect.bottom + dropdownHeight > visibleBottom - 20;
            return {
                position: "fixed",
                minWidth: `${minWidth}px`,
                width: `${rect.width}px`,
                ...(wouldOverflowBottom
                    ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                    : { top: rect.bottom + 4 }),
                left: rect.left,
            };
        };

        return (
            <div ref={containerRef} className="relative">
                {/* Borderless input — border only on focus */}
                <input
                    ref={ref}
                    type="text"
                    value={inputValue}
                    autoComplete="on"
                    enterKeyHint={onEnterNext ? "next" : undefined}
                    placeholder={ placeholder}
                    className={cn(
                        "w-full text-xs font-semibold text-foreground bg-transparent transition-all duration-150",
                        "outline-none px-1 py-0.5",
                        "outline-none placeholder:text-muted-foreground/40 placeholder:font-normal",
                        className
                    )}
                    onChange={(e) => {
                        const val = e.target.value;
                        setInputValue(val);
                        setSearchQuery(val);  // only user typing drives the search
                        onCustomInput?.(val);
                        setArrowed(false);
                        setIsOpen(val.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH);
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        if (inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH)
                            setIsOpen(true);
                    }}
                    onClick={() => {
                        if (inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH)
                            setIsOpen(true);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                        setTimeout(() => setIsOpen(false), 200);
                    }}
                    onKeyDown={(e) => {
                        if (!isOpen && drugs.length > 0 &&
                            inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH) {
                            setIsOpen(true);
                            setHighlightedIndex(0);
                            return;
                        }
                        if (!isOpen) {
                            if (e.key === "Enter" && onEnterNext) { e.preventDefault(); onEnterNext(); }
                            return;
                        }
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            // First arrow with no drug listed lands on the first template
                            setHighlightedIndex((p) => (drugs.length === 0 && !arrowed ? 0 : Math.min(p + 1, optionCount - 1)));
                            setArrowed(true);
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightedIndex((p) => Math.max(p - 1, 0));
                            setArrowed(true);
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            if (drugs.length > 0 && highlightedIndex >= 0 && highlightedIndex < drugs.length) {
                                const drug = drugs[highlightedIndex];
                                setInputValue(drug.brand_name);
                                setSearchQuery("");   // stop searching after keyboard selection too
                                onChange(drug);
                                setIsOpen(false);
                                if (onEnterNext) setTimeout(onEnterNext, 100);
                            } else if (arrowed && highlightedIndex >= drugs.length && highlightedIndex < optionCount) {
                                pickTemplate(templates[highlightedIndex - drugs.length].id);
                            } else if (onEnterNext) {
                                onEnterNext();
                            }
                        } else if (e.key === "Escape") {
                            setIsOpen(false);
                        } else if (e.key === "Tab") {
                            setIsOpen(false);
                        }
                    }}
                />

                { !showDropdown && subtitle && (
                    <span
                        className="absolute left-1 right-1 text-2xs text-muted-foreground/70 italic leading-none truncate pointer-events-none z-10"
                        style={{ top: "100%", paddingTop: 2 }}
                        title={subtitle}
                    >
                        {subtitle}
                    </span>
                )}

                {showDropdown && createPortal(
                    <div
                        ref={dropdownRef}
                        // pointer-events-auto: inside a dialog the body takes no clicks, and this list is portalled to the body
                        className="z-[9999] pointer-events-auto bg-popover shadow-lg border border-border rounded-md max-h-64 overflow-auto text-sm ring-1 ring-border"
                        style={getDropdownStyle()}
                    >
                        {isLoading && (
                            <div className="px-3 py-4 flex items-center justify-center">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </div>
                        )}

                        {!isLoading && searchResults.map((drug) => {
                            if (justHidden.includes(drug.id)) return (
                                <div key={drug.id} className="px-3 py-2 flex items-center justify-between gap-2 border-b border-border/40 last:border-0 bg-muted/40 text-muted-foreground">
                                    <span className="text-sm truncate line-through decoration-muted-foreground/50">{drug.brand_name}</span>
                                    <span className="flex items-center gap-2 shrink-0 text-xs">
                                        Hidden
                                        <button
                                            type="button"
                                            onMouseDown={(e) => handleUndoHide(drug.id, e)}
                                            className="font-medium text-primary hover:underline"
                                        >
                                            Undo
                                        </button>
                                    </span>
                                </div>
                            );
                            const i = drugs.indexOf(drug);
                            if (i === -1) return null;
                            return (
                            <div
                                key={drug.id}
                                ref={(el) => { optionRefs.current[i] = el; }}
                                className={cn(
                                    "px-3 py-2 cursor-pointer transition-colors border-b border-border/40 last:border-0",
                                    i === highlightedIndex
                                        ? "bg-primary text-primary-foreground"
                                        : "hover:bg-accent text-foreground"
                                )}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                onMouseDown={(e) => handleSelect(drug, e)}
                            >
                                <div className="flex items-center gap-1.5 justify-between">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        <span className="font-semibold text-sm truncate">{drug.brand_name}</span>
                                        {isFav(drug) && (
                                            <Star
                                                className="h-3 w-3 shrink-0 fill-warning text-warning"
                                                aria-label="In My medicines"
                                            />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        {starFeedback?.id === drug.id ? (
                                            <span className="text-2xs font-medium mr-1">
                                                {starFeedback.added ? "Added to My medicines" : "Removed from My medicines"}
                                            </span>
                                        ) : drug.form && (
                                            <span className="text-2xs font-medium px-1.5 py-0.5 mr-1 rounded bg-current/10 capitalize">
                                                {drug.form}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            onMouseDown={(e) => handleToggleFavourite(drug, e)}
                                            title={isFav(drug) ? "Remove from My medicines" : "Add to My medicines"}
                                            aria-label={isFav(drug) ? "Remove from My medicines" : "Add to My medicines"}
                                            className={cn(rowActionClass(i === highlightedIndex), isFav(drug) && "text-warning hover:text-warning")}
                                        >
                                            <Star className={cn("h-4 w-4", isFav(drug) && "fill-current")} />
                                        </button>
                                        <button
                                            type="button"
                                            onMouseDown={(e) => handleHide(drug, e)}
                                            title="Hide from my search"
                                            aria-label="Hide from my search"
                                            className={rowActionClass(i === highlightedIndex)}
                                        >
                                            <EyeOff className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                                {drug.composition_raw && (
                                    <div className={cn(
                                        "text-xs mt-0.5",
                                        i === highlightedIndex ? "text-primary-foreground/75" : "text-muted-foreground"
                                    )}>
                                        {drug.composition_raw}
                                    </div>
                                )}
                            </div>
                            );
                        })}

                        {templates.map((t, ti) => {
                            const active = arrowed && drugs.length + ti === highlightedIndex;
                            return (
                                <div
                                    key={t.id}
                                    className={cn(
                                        "px-3 py-2 cursor-pointer transition-colors border-b border-border/40 last:border-0 flex items-center gap-2 min-w-0",
                                        active ? "bg-primary text-primary-foreground" : "hover:bg-accent text-foreground"
                                    )}
                                    onMouseDown={(e) => { e.preventDefault(); pickTemplate(t.id); }}
                                >
                                    <span className="shrink-0 rounded bg-current/10 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide">Template</span>
                                    <span className="shrink-0 font-semibold text-sm">{t.title}</span>
                                    <span className={cn("truncate text-xs", active ? "text-primary-foreground/75" : "text-muted-foreground")}>{t.hint}</span>
                                </div>
                            );
                        })}

                        {!isLoading && favTipDrug && (
                            <div
                                className="px-3 py-2 flex items-center gap-2 border-t border-warning/30 bg-warning/5 text-xs text-foreground/90"
                                onMouseDown={(e) => e.preventDefault()}  // keep focus in the input
                            >
                                <Lightbulb className="h-3.5 w-3.5 shrink-0 text-warning" />
                                <span className="flex-1 min-w-0">
                                    You prescribe <span className="font-semibold">{favTipDrug.brand_name}</span> often. Tap
                                    <Star className="inline h-3 w-3 mx-0.5 -mt-0.5" /> to keep it at the top of your search.
                                </span>
                                <button
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); tips.markSeen(FAV_TIP_ID); }}
                                    className="shrink-0 font-medium text-primary hover:underline"
                                >
                                    Got it
                                </button>
                            </div>
                        )}

                        {!isLoading && drugs.length === 0 && justHidden.length === 0 && (
                            <div className="px-3 pt-3 pb-1 text-muted-foreground text-center text-xs italic">
                                No results found
                            </div>
                        )}

                        {/* Offered whenever no result is the typed name — fuzzy search rarely comes back empty */}
                        {!isLoading && !drugs.some((d) => d.brand_name.trim().toLowerCase() === typedName) && (
                            <div
                                className="sticky bottom-0 px-3 py-2 cursor-pointer text-xs text-primary bg-popover hover:bg-accent flex items-center gap-1.5 border-t border-border"
                                onMouseDown={handleAddToMyMedicines}
                            >
                                {adding ? <Loader2 className="h-3 w-3 shrink-0 animate-spin" /> : <Plus className="h-3 w-3 shrink-0" />}
                                <span className="truncate">Not listed? Add “{inputValue.trim()}” to My medicines</span>
                            </div>
                        )}

                    </div>,
                    document.body
                )}
            </div>
        );
    }
);

DrugSearchInput.displayName = "DrugSearchInput";
export default DrugSearchInput;